// A bot's line about this week's game, on the matchup (AI_MANAGERS.md §6.5).
//
// WORKED OUT, NEVER STORED. Every member reads the same line from the
// matchup and its score, which the screen already reads; nothing is written,
// so the liveliness costs nothing per reader (DIRECTION D3).
//
// THE SAME LINE FOR EVERYBODY, AND NOT THE SAME TWO WEEKS RUNNING. Whether
// the bot speaks this week is one draw keyed by the league, the team and the
// week; which line is a draw keyed by the league, the team and the moment,
// moved on one each week. A reload never changes it. It changes when the
// games start, when the lead changes hands, and at the final.
//
// NEVER ADVICE (§6.2): before the games it speaks of the week and the
// opponent, never of the other side's players, and the cast's test holds
// every line to the notifications' list of imperatives.

import type { CastMember, Occasion } from './cast';
import { draw } from './draw';
import { fill, resultOccasion } from './talk';

/** Where the game stands, as the matchup reads it. */
export type GameState = 'pre' | 'live' | 'final';

export interface MatchupLineAsk {
  member: CastMember;
  /** The seat's talkativeness (`dials.trashTalk`), 0 to 1. */
  trashTalk: number;
  leagueId: string;
  teamId: string;
  week: number;
  state: GameState;
  /**
   * A playoff or consolation game: its final has no line, as the chat's
   * results do not (`bot-talk.ts`). A knocked-out bot saying "next week" is
   * untrue, and a two-week final's first leg decides nothing.
   */
  postseason: boolean;
  /** This bot's score and its opponent's, as shown. */
  score: number;
  theirs: number;
  /** Its own team's name, the opponent's, and the opponent's manager's. */
  team: string;
  opponent: string;
  owner: string;
}

/** How likely a bot speaks on the matchup in a week: half, plus half its dial. */
export function speaksChance(trashTalk: number): number {
  return 0.5 + 0.5 * trashTalk;
}

const tenths = (n: number) => Math.round(n * 10) / 10;

/** Which moment the game is at, or null for one with nothing to say: a tie at the final. */
export function matchupOccasion(state: GameState, score: number, theirs: number): Occasion | null {
  const mine = tenths(score);
  const other = tenths(theirs);
  if (state === 'pre') return 'preview';
  // A LEVEL SCORE WHILE IT IS PLAYED, 0–0 included, keeps the preview's line.
  if (state === 'live') return mine > other ? 'leading' : mine < other ? 'trailing' : 'preview';
  return resultOccasion(mine, other);
}

/** The bot's line about its game this week, or null when it is quiet. */
export function matchupLine(ask: MatchupLineAsk): string | null {
  const { leagueId, teamId, week } = ask;
  if (draw(`${leagueId}|${teamId}|${String(week)}|matchup`) >= speaksChance(ask.trashTalk))
    return null;
  if (ask.state === 'final' && ask.postseason) return null;
  const occasion = matchupOccasion(ask.state, ask.score, ask.theirs);
  if (occasion === null) return null;
  const pool = ask.member.lines[occasion];
  const first = Math.floor(draw(`${leagueId}|${teamId}|${occasion}|matchup`) * pool.length);
  const line = pool[(first + week) % pool.length]!;
  const mine = tenths(ask.score);
  const other = tenths(ask.theirs);
  return fill(line, {
    team: ask.team,
    opponent: ask.opponent,
    owner: ask.owner,
    week,
    score: mine.toFixed(1),
    theirs: other.toFixed(1),
    margin: Math.abs(mine - other).toFixed(1),
  });
}
